/**
 * Cobros: registrar y marcar cobrado.
 *
 * Es el bucle diario de administración y no había forma de hacerlo. Se podía
 * crear un cobro por API y marcarlo cobrado **si se conocía su UUID de
 * antemano**, porque tampoco existía forma de listarlos.
 *
 * Deliberadamente NO incluye alta de cuentas bancarias ni de mandatos SEPA.
 * El IBAN se guarda hoy con un marcador —`enc:v1:` sobre base64— que la
 * propia documentación identifica como pendiente de la capa real de cifrado
 * con KMS (SECURITY.md §4). Registrar cobros no necesita IBAN; cargar las
 * cuentas del banco sí, y hacerlo antes de que ese cifrado exista sería
 * meter datos bancarios reales en un sitio donde cualquiera con acceso a la
 * base los lee.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { FormSection, SelectField, TextField } from '../forms/fields.js';

interface Payment {
  id: string;
  direction: string;
  method: string;
  status: string;
  amount: string;
  currency: string;
  due_date: string;
  value_date: string | null;
  reference: string | null;
  customer_name: string | null;
  supplier_name: string | null;
}

interface Customer {
  id: string;
  legal_name: string;
}

const METHODS = [
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'DOMICILIACION', label: 'Domiciliación' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'PAGARE', label: 'Pagaré' },
  { value: 'COMPENSACION', label: 'Compensación' },
  { value: 'OTROS', label: 'Otros' },
];

const STATUS_LABEL: Record<string, string> = {
  PREVISTO: 'Previsto',
  PRESENTADO: 'Presentado',
  COBRADO: 'Cobrado',
  DEVUELTO: 'Devuelto',
  ANULADO: 'Anulado',
};

export function TreasuryActions(): React.ReactNode {
  const { api, user } = useSession();

  const [payments, setPayments] = useState<Payment[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [customerId, setCustomerId] = useState('');
  const [method, setMethod] = useState('TRANSFERENCIA');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [reference, setReference] = useState('');

  const canWrite = hasPermission(user, 'treasury.create');
  const canUpdate = hasPermission(user, 'treasury.update');

  const reload = useCallback(async () => {
    const [p, c] = await Promise.all([
      api.get<{ items: Payment[] }>('/treasury/payments?direction=COBRO').catch(() => ({ items: [] })),
      api
        .get<{ items: Customer[] }>('/customers?limit=200')
        .catch(() => ({ items: [] as Customer[] })),
    ]);
    setPayments(p.items);
    setCustomers(c.items ?? []);
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function create(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await api.post('/treasury/payments', {
        direction: 'COBRO',
        method,
        amount,
        dueDate,
        ...(customerId === '' ? {} : { customerId }),
        ...(reference === '' ? {} : { reference }),
      });
      setNotice('Cobro registrado.');
      setAmount('');
      setReference('');
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido registrar el cobro.');
    } finally {
      setBusy(false);
    }
  }

  async function settle(payment: Payment): Promise<void> {
    const today = new Date().toISOString().slice(0, 10);
    const valueDate = globalThis.prompt(
      `Fecha valor del cobro de ${payment.amount} ${payment.currency}:`,
      today,
    );
    if (valueDate === null || valueDate === '') return;

    setError(null);
    setBusy(true);
    try {
      await api.post(`/treasury/payments/${payment.id}/settle`, { valueDate });
      setNotice('Cobro marcado como cobrado.');
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido marcar el cobro.');
    } finally {
      setBusy(false);
    }
  }

  if (!canWrite && !canUpdate) return null;

  const pending = payments.filter((p) => p.status === 'PREVISTO' || p.status === 'PRESENTADO');

  return (
    <>
      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}

      <section className="panel">
        <h3 className="panel__title">Cobros pendientes ({pending.length})</h3>

        {payments.length === 0 ? (
          <p className="screen__note">No hay ningún cobro registrado.</p>
        ) : (
          <div className="scroller">
            <table className="table__grid table__grid--compact">
              <thead>
                <tr>
                  <th>Vence</th>
                  <th>Cliente</th>
                  <th>Importe</th>
                  <th>Forma</th>
                  <th>Estado</th>
                  <th>Fecha valor</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td>{p.due_date}</td>
                    <td>{p.customer_name ?? p.supplier_name ?? '—'}</td>
                    <td>
                      {p.amount} {p.currency}
                    </td>
                    <td>{METHODS.find((m) => m.value === p.method)?.label ?? p.method}</td>
                    <td>
                      {p.status === 'COBRADO' ? (
                        <span className="pill pill--ok">✓ {STATUS_LABEL[p.status]}</span>
                      ) : p.status === 'DEVUELTO' ? (
                        <span className="pill pill--bad">{STATUS_LABEL[p.status]}</span>
                      ) : (
                        <span className="pill pill--info">{STATUS_LABEL[p.status] ?? p.status}</span>
                      )}
                    </td>
                    <td>{p.value_date ?? '—'}</td>
                    <td>
                      {(p.status === 'PREVISTO' || p.status === 'PRESENTADO') && canUpdate && (
                        <button
                          className="button button--quiet"
                          type="button"
                          disabled={busy}
                          onClick={() => void settle(p)}
                        >
                          Marcar cobrado
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {canWrite && (
        <section className="panel">
          <h3 className="panel__title">Registrar cobro</h3>
          <div className="form">
            <FormSection title="Datos">
              <SelectField
                label="Cliente"
                value={customerId}
                onChange={setCustomerId}
                options={customers.map((c) => ({ value: c.id, label: c.legal_name }))}
                emptyLabel="Sin cliente asociado"
              />
              <SelectField label="Forma de cobro" value={method} onChange={setMethod} options={METHODS} />
              <TextField
                label="Importe"
                value={amount}
                onChange={setAmount}
                help="En euros, con dos decimales."
                required
              />
              <TextField
                label="Fecha de vencimiento"
                type="date"
                value={dueDate}
                onChange={setDueDate}
                required
              />
              <TextField label="Referencia" value={reference} onChange={setReference} />
              <button
                className="button button--primary"
                type="button"
                disabled={busy || amount === '' || dueDate === ''}
                onClick={() => void create()}
              >
                {busy ? 'Guardando…' : 'Registrar cobro'}
              </button>
            </FormSection>
          </div>
        </section>
      )}

      <p className="screen__note">
        Las <strong>cuentas bancarias y los mandatos SEPA</strong> no se dan de alta aquí a
        propósito: el IBAN se guarda hoy con un marcador pendiente de la capa real de cifrado
        (SECURITY.md §4). Registrar cobros no necesita IBAN; cargar las cuentas del banco sí, y
        conviene esperar a que ese cifrado exista.
      </p>
    </>
  );
}
